import React, { useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { selectServerById, setEditId } from "../../../store/mine_craft/serversSlice";
import { destroyServer, fetchServer } from "../../../actions/mine_craft/serversActions";
import minecraftUrls from "../../../configs/mine_craft/minecraftUrls";

import { LikeOutlined, MessageOutlined, StarOutlined } from "@ant-design/icons";
import { message } from "antd";
import Tabs from "../../../componets/Tabs";
import PageLayout from "../_partials/PageLayout";
import IconText from "src/componets/IconText";
import StatsIcons from "src/componets/StatsIcons";
import Edit from "./Edit";
import SummaryShow from "src/componets/SummaryShow";
import SidebarShow from "./_partials/_SidebarShow";
import ServerMeta from "./_partials/_ServerMeta";
import Details from "./_partials/_Details";
import Members from "./_partials/_Members";
import Comments from "./_partials/_Comments";
import Content from "./_partials/_Content";
import Server from "./_partials/_Server";
import Backups from "./_partials/_Backups";
import Activity from "./_partials/_Activity";
import Schedules from "./_partials/_Schedules";
import Worlds from "./_partials/_Worlds";
import EditComment from "../Comments/Edit";
import { ButtonDestroy, ButtonEdit } from "../../../componets/Buttons";
import usePermissions from "src/hooks/usePermissions";
import './style.scss';

// Provisioning is async — crafty-servers reports ready/failed through a
// webhook (with a 1-minute sync fallback), so while the record says
// `installing` the page re-reads it to catch the flip without a refresh.
const PROVISION_POLL_MS = 10000;

const Show: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const server = useAppSelector(state => id ? selectServerById(state, id) : undefined);
  const { write_minecraft } = usePermissions();

  const hostedState = server?.hosted_server?.state;
  // Which server the last observed state belonged to — navigating between
  // servers must not read one server's `installing` as another's history.
  const prevHosted = useRef<{ id?: string, state?: string }>({});

  useEffect(() => {
    if (id) {
      dispatch(fetchServer(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (!id || hostedState !== 'installing') return;

    const timer = setInterval(() => {
      dispatch(fetchServer(id)).catch(console.error);
    }, PROVISION_POLL_MS);

    return () => clearInterval(timer);
  }, [id, hostedState, dispatch]);

  // The flip out of `installing` can land while any tab is open — a toast
  // says so even when the Server tab (pill + banner) isn't visible.
  useEffect(() => {
    const previous = prevHosted.current;

    prevHosted.current = { id, state: hostedState };

    if (previous.id !== id || previous.state !== 'installing') return;

    if (hostedState === 'ready')  message.success('Server provisioned — it is now live.');
    if (hostedState === 'failed') message.error('Server provisioning failed — see the Server tab for the error.');
  }, [id, hostedState]);

  function onClickEdit() {
    if (server) {
      dispatch(setEditId(server.id));
    }
  }

  function onClickDestroy() {
    if (server) {
      handleDestroy(server.id).catch(console.error);
    }
  }

  async function handleDestroy (id: any) {
    const resultAction = await dispatch(destroyServer(id));

    if (destroyServer.fulfilled.match(resultAction)) {
      message.success(`ItemID ${id} was successful deleted`);
      setTimeout(() => navigate(minecraftUrls.serversPath()), 1000);
    }

    if (destroyServer.rejected.match(resultAction)) {
      message.error(resultAction.error.message);
    }
  }

  return (
    <PageLayout className='v-servers-show'>
      <div className='v-container-fluid'>
        <div className='o-header'>
          {
            server &&
              <SummaryShow
                title={server.title}
                subtitle={`${server.ip}:${server.port}`}
                image={server.image_url}
                createdAt={server.created_at}
                updatedAt={server.updated_at}
                className='v-mb-15'
                actions={write_minecraft ? [
                  <ButtonEdit key='button-edit' onClick={onClickEdit} />,
                  <ButtonDestroy key='button-destroy' onClick={onClickDestroy} />
                ] : []}
              />
          }

          <StatsIcons
            icons={[
              <IconText tooltip='popular' icon={StarOutlined} text={server?.popular_count} />,
              <IconText tooltip='likes' icon={LikeOutlined} text={server?.likes_count} />,
              <IconText tooltip='comments' icon={MessageOutlined} text={server?.comments_count} />
            ]}
          />
        </div>

        {/* The fixed right sidebar is hidden under 768px — the same metadata
            block renders inline here so nothing becomes unreachable. */}
        <div className='v-srv-meta-inline'>
          <ServerMeta server={server} />
        </div>

        {/* Rendered only once the server arrives: the tab set depends on
            hosted_server, and mounting early would lock antd's default
            active tab to whatever pane happened to exist first. */}
        {server &&
          <Tabs
            paramsKey='tab'
            items={[
              ...(server.hosted_server
                ? [
                    { label: 'Server', key: 'server', children: <Server server={server} /> },
                    { label: 'Content', key: 'content', children: <Content server={server} /> },
                    { label: 'Backups', key: 'backups', children: <Backups server={server} /> },
                    { label: 'Worlds', key: 'worlds', children: <Worlds server={server} /> },
                    { label: 'Members', key: 'members', children: <Members server={server} /> },
                    { label: 'Activity', key: 'activity', children: <Activity server={server} /> },
                    { label: 'Schedules', key: 'schedules', children: <Schedules server={server} /> }
                  ]
                : []),
              { label: 'Details', key: 'details', children: <Details server={server} /> },
              { label: 'Comments', key: 'comment', children: <Comments serverId={server.id} /> }
            ]}
          />
        }
      </div>

      <SidebarShow server={server} />

      <Edit />

      <EditComment />
    </PageLayout>
  )
}

export default Show;
